import React, { useState } from 'react';
import {
	AppBar,
	Avatar,
	Box,
	Button,
	Stack,
	Tab,
	Tabs,
	Toolbar,
	Typography
} from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthService from '../../../services/authService';
import ServiceTypeList from './queue/serviceTypes/ServiceTypeList';
import ManagerList from './queue/managers/ManagerList';
import AdManagement from './queue/ads/AdManagement';
import CaptchaSettings from './queue/captcha/CaptchaSettings';
import TicketManagement from './queue/tickets/TicketManagement';
import QueueMonitoring from './queue/monitoring/QueueMonitoring';
import LanguageSwitcher from '../../../common/LanguageSwitcher/LanguageSwitcher';
import './admin-queue-cabinet.scss';

const TAB_SX = {
	color: 'inherit',
	fontWeight: 600,
	opacity: 0.85,
	textTransform: 'none',
	fontSize: 15
};

function AdminQueueCabinet() {
	const { t } = useTranslation();
	AuthService.checkPermissionToPage(['ADMIN', 'MODERATOR']);

	const [tab, setTab] = useState(5);

	const rawUser = AuthService.getCurrentUser();
	const user = rawUser
		? typeof rawUser === 'string'
			? JSON.parse(rawUser)
			: rawUser
		: {};

	return (
		<Box className='admin-queue-cabinet'>
			{/* ─── AppBar ─────────────────────────────────────────────── */}
			<AppBar position='sticky' elevation={0} className='aqc-appbar'>
				<Toolbar className='aqc-toolbar'>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
							<AdminPanelSettingsRoundedIcon />
						</Avatar>
						<Box>
							<Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1 }}>
								{user?.surname} {user?.name}
							</Typography>
							<Typography variant='caption' color='inherit' sx={{ opacity: 0.8 }}>
								{t('admin.role')}
							</Typography>
						</Box>
					</Stack>

					<Stack direction='row' spacing={1.25} alignItems='center'>
						<LanguageSwitcher variant='dark' />
						<Button
							component={RouterLink}
							to='/'
							variant='outlined'
							color='inherit'
							startIcon={<HomeRoundedIcon />}
							sx={{
								textTransform: 'none',
								fontWeight: 700,
								borderRadius: 2,
								height: 36,
								px: 1.5,
								borderColor: 'rgba(255,255,255,0.55)',
								'&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
							}}
						>
							{t('common.home')}
						</Button>
						<Button
							onClick={AuthService.logout}
							variant='outlined'
							color='inherit'
							startIcon={<LogoutRoundedIcon />}
							sx={{
								textTransform: 'none',
								fontWeight: 700,
								borderRadius: 2,
								height: 36,
								px: 1.5,
								borderColor: 'rgba(255,255,255,0.55)',
								'&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
							}}
						>
							{t('common.logout')}
						</Button>
					</Stack>
				</Toolbar>

				{/* Tabs */}
				<Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.2)', px: 3 }}>
					<Tabs
						value={tab}
						onChange={(_, v) => setTab(v)}
						textColor='inherit'
						TabIndicatorProps={{ style: { backgroundColor: '#fff' } }}
					>
						<Tab label={t('admin.tabs.serviceTypes')} sx={TAB_SX} />
						<Tab label={t('admin.tabs.managers')}    sx={TAB_SX} />
						<Tab label={t('admin.tabs.ads')}         sx={TAB_SX} />
						<Tab label={t('admin.tabs.settings')}    sx={TAB_SX} />
						<Tab label={t('admin.tabs.tickets')}     sx={TAB_SX} />
						<Tab label={t('admin.tabs.monitoring')}  sx={TAB_SX} />
					</Tabs>
				</Box>
			</AppBar>

			{/* ─── Content ────────────────────────────────────────────── */}
			<Box className='aqc-content'>
				{tab === 0 && <ServiceTypeList />}
				{tab === 1 && <ManagerList />}
				{tab === 2 && <AdManagement />}
				{tab === 3 && <CaptchaSettings />}
				{tab === 4 && <TicketManagement />}
				{tab === 5 && <QueueMonitoring />}
			</Box>
		</Box>
	);
}

export default AdminQueueCabinet;
